import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Globe2, GraduationCap, FileText } from "lucide-react";

const topics = ["Study in China – Universities", "Scholarships (CSC / University)", "Application & Visa Guidance", "HSK Test Preparation", "General Enquiry"];

export default function Consultation() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: topics[0], message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consultations", form);
      setSent(true);
      toast.success("Enquiry submitted! Our counsellor will contact you soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[80vh]">
      <div className="grain bg-ink text-rice px-6 lg:px-16 py-20 flex flex-col justify-center">
        <span className="label text-gold">Free Consultation</span>
        <h1 className="font-serif font-light text-5xl lg:text-6xl tracking-tighter mt-4 text-rice">Study in China</h1>
        <p className="text-rice/60 mt-6 max-w-md leading-relaxed">
          Dreaming of studying at a top Chinese university? Our counsellors guide you through programs,
          scholarships, applications and visas — end to end.
        </p>
        <div className="mt-12 space-y-6 max-w-md">
          {[
            { icon: GraduationCap, t: "University Selection", d: "Match your goals to the right institutions." },
            { icon: FileText, t: "Scholarships", d: "CSC, Confucius Institute & university funding." },
            { icon: Globe2, t: "Applications & Visa", d: "Step-by-step support until you land." },
          ].map((x, i) => (
            <div key={i} className="flex gap-4">
              <x.icon className="text-gold shrink-0" size={22} />
              <div>
                <p className="font-display font-semibold text-rice">{x.t}</p>
                <p className="text-sm text-rice/50">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 lg:px-16 py-20 flex items-center bg-rice">
        <div className="w-full max-w-md">
          {sent ? (
            <div className="border border-black/10 bg-white p-10 text-center" data-testid="consult-success">
              <div className="w-14 h-14 bg-cinnabar text-rice mx-auto flex items-center justify-center font-serif text-2xl mb-5">谢</div>
              <h2 className="font-serif text-3xl">Thank you!</h2>
              <p className="text-clay mt-3">Your enquiry has been received. Our counsellor will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="consult-form">
              <h2 className="font-serif text-3xl mb-2">Request a call back</h2>
              {["name", "email", "phone"].map((f) => (
                <div key={f}>
                  <label className="label text-clay">{f}</label>
                  <input required={f !== "phone"} type={f === "email" ? "email" : "text"}
                    value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    data-testid={`consult-${f}`}
                    className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
                </div>
              ))}
              <div>
                <label className="label text-clay">Topic</label>
                <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="consult-topic"
                  className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar">
                  {topics.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label text-clay">Message</label>
                <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="consult-message"
                  className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
              </div>
              <button disabled={busy} data-testid="consult-submit"
                className="w-full py-4 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300 disabled:opacity-60">
                {busy ? "Submitting…" : "Submit Enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
